import { Button, Modal } from '@mantine/core';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  ContactDeletionBlockerDto,
  ContactDeletionBlockerDtoTypeEnum,
} from 'src/Services/API/generated';

import styles from './BlockedContactDeletionModal.module.scss';
import { Props } from './BlockedContactDeletionModal.types';

const SECTION_TITLES: Record<ContactDeletionBlockerDtoTypeEnum, string> = {
  [ContactDeletionBlockerDtoTypeEnum.ExpensePaid]: 'Dépenses payées',
};

const SECTION_ORDER: ContactDeletionBlockerDtoTypeEnum[] = [
  ContactDeletionBlockerDtoTypeEnum.ExpensePaid,
];

const buildBlockerRoute = (assetId: string, blocker: ContactDeletionBlockerDto): string | null => {
  switch (blocker.type) {
    case ContactDeletionBlockerDtoTypeEnum.ExpensePaid:
      return `/asset/${assetId}/expenses/${blocker.entityId}`;
    default:
      return null;
  }
};

const formatDate = (date?: Dayjs): string => (date ? date.format('L') : '');

const BlockedContactDeletionModal = ({ open, onClose, contactName, blockers, assetId }: Props) => {
  const { t } = useTranslation();

  const grouped = SECTION_ORDER.map(type => ({
    type,
    items: blockers.filter(b => b.type === type),
  })).filter(group => group.items.length > 0);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('contacts.deletion.blockedTitle', 'Impossible de supprimer ce contact')}
      size="lg"
    >
      <div className={styles.intro}>
        {t(
          'contacts.deletion.blockedIntro',
          "{{name}} est référencé dans les éléments suivants. Pour pouvoir le supprimer, résolvez ou supprimez d'abord ces éléments.",
          { name: contactName }
        )}
      </div>

      {grouped.map(group => (
        <div key={group.type} className={styles.section}>
          <div className={styles.sectionTitle}>
            {SECTION_TITLES[group.type]} ({group.items.length})
          </div>
          {group.items.map(blocker => {
            const route = buildBlockerRoute(assetId, blocker);
            const dateText = formatDate(blocker.date);

            if (route) {
              return (
                <Link
                  key={`${blocker.type}-${blocker.entityId}`}
                  to={route}
                  className={styles.blockerItem}
                  onClick={onClose}
                >
                  <span className={styles.blockerLabel}>{blocker.label}</span>
                  {dateText && <span className={styles.blockerDate}>{dateText}</span>}
                </Link>
              );
            }

            return (
              <div key={`${blocker.type}-${blocker.entityId}`} className={styles.staticBlocker}>
                {blocker.label}
              </div>
            );
          })}
        </div>
      ))}

      <div className={styles.buttons}>
        <Button onClick={onClose}>{t('common.understood', 'Compris')}</Button>
      </div>
    </Modal>
  );
};

export default BlockedContactDeletionModal;
